import { Card, CardBody, Skeleton } from "@heroui/react";
import { useRoles, usePermissions } from "@src/presentation/hooks/rbac";
import { Shield, Lock, TrendingUp, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@src/presentation/hooks/usePermission";
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder";

export function RbacInsightsCard() {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const canViewStats = hasPermission("stats:read");

    // Early return if no permission - prevents API calls
    if (!canViewStats) {
        return <PermissionPlaceholder title="RBAC Insights" description="You need 'stats:read' permission to view RBAC insights." />;
    }

    const { data: roles = [], isLoading: rolesLoading } = useRoles();
    const { data: permissions = [], isLoading: permissionsLoading } = usePermissions();

    const isLoading = rolesLoading || permissionsLoading;

    // Calculate insights
    const activeRoles = roles.filter((r) => r.isActive).length;
    const inactiveRoles = roles.filter((r) => !r.isActive).length;
    const rolesWithNoPermissions = roles.filter((r) => r.permissions.length === 0).length;
    const avgPermissionsPerRole = roles.length > 0
        ? (roles.reduce((sum, r) => sum + r.permissions.length, 0) / roles.length).toFixed(1)
        : 0;

    // Find most used permissions
    const permissionUsage = new Map<string, number>();
    roles.forEach((role) => {
        role.permissions.forEach((perm) => {
            permissionUsage.set(perm, (permissionUsage.get(perm) || 0) + 1);
        });
    });

    const topPermissions = Array.from(permissionUsage.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    if (isLoading) {
        return (
            <Card className="border border-border rounded shadow-none">
                <CardBody className="p-6">
                    <Skeleton className="h-6 w-32 rounded mb-4" />
                    <div className="space-y-3">
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                    </div>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="border border-border rounded shadow-none hover:shadow-custom transition-shadow">
            <CardBody className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                            <TrendingUp className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">RBAC Insights</h2>
                            <p className="text-sm text-muted-foreground">Access control analytics</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/rbac/roles")}
                        className="text-xs font-medium text-primary hover:underline"
                    >
                        View Details
                    </button>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="p-3 rounded border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                            <Shield className="h-4 w-4 text-success" />
                            <p className="text-xs text-muted-foreground">Active Roles</p>
                        </div>
                        <p className="text-2xl font-bold text-success">{activeRoles}</p>
                    </div>
                    <div className="p-3 rounded border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="h-4 w-4 text-default-500" />
                            <p className="text-xs text-muted-foreground">Inactive Roles</p>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{inactiveRoles}</p>
                    </div>
                    <div className="p-3 rounded border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                            <Lock className="h-4 w-4 text-primary" />
                            <p className="text-xs text-muted-foreground">Avg Permissions</p>
                        </div>
                        <p className="text-2xl font-bold text-primary">{avgPermissionsPerRole}</p>
                    </div>
                    <div className="p-3 rounded border border-warning/50 bg-warning/5">
                        <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="h-4 w-4 text-warning" />
                            <p className="text-xs text-warning">No Permissions</p>
                        </div>
                        <p className="text-2xl font-bold text-warning">{rolesWithNoPermissions}</p>
                    </div>
                </div>

                {/* Top Permissions */}
                {topPermissions.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold text-foreground mb-3">Most Used Permissions</h3>
                        <div className="space-y-2">
                            {topPermissions.map(([permission, count]) => {
                                const permObj = permissions.find((p) => p.name === permission);
                                return (
                                    <div
                                        key={permission}
                                        className="flex items-center justify-between p-2 rounded border border-border bg-background hover:border-primary/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Lock className="h-3 w-3 text-default-400" />
                                            <div>
                                                <p className="text-xs font-mono font-medium text-foreground">
                                                    {permission}
                                                </p>
                                                {permObj && (
                                                    <p className="text-xs text-muted-foreground">
                                                        {permObj.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">
                                                {count} {count === 1 ? "role" : "roles"}
                                            </span>
                                            <div className="w-16 h-1.5 bg-default-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-primary rounded-full"
                                                    style={{
                                                        width: `${(count / roles.length) * 100}%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
